'use client';

import { useDynamicConfig } from '@statsig/react-bindings';
import noop from 'lodash/noop';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import logToDatadog from '@/logger/logToDatadog';
import { type RefreshCartResult } from '@/queries/RefreshCart';
import { useRefreshCart } from '@/queries/RefreshCart';
import {
  performSavePromotion,
  type SavePromotionResult,
  useSavePromotion,
} from '@/queries/SavePromotion';
import {
  readFromLocalStorage,
  writeToLocalStorage,
} from '@/utils/localStorage';

import { PROMOTION_CODE } from '../[locale]/store/_consts/discounts';

export interface CartContextValues {
  handleSetUrlDiscountCode: (code: string) => void;
  maxAddOnQuantityReachedByBaseSku: Record<string, boolean>;
  maxChargerQuantityReached: boolean;
  maxRingQuantityReached: boolean;
  refreshCart: RefreshCartResult | undefined;
  refreshCartError: Error | null | undefined;
  refreshCartLoading: boolean;
  refreshCartSuccess: boolean;
  sitewideDiscounts: (SavePromotionResult | null)[];
  urlPromotionData: SavePromotionResult | null | undefined;
  urlPromotionError: Error | null | undefined;
  urlPromotionLoading: boolean;
  urlPromotionSuccess: boolean;
  urlDiscountCode: string;
}

export const DEFAULT_REFRESH_CART_VALUES: RefreshCartResult = {
  cart: {
    refreshCart: {
      cart: {
        chargerCount: 0,
        creationTags: null,
        digitalSized: null,
        isWarrantyReplacement: null,
        itemCountForIcon: 0,
        lastUpdatedAt: null,
        lineItems: [],
        maxAddOnQuantityByBaseSku: {},
        maxChargerQuantity: 10,
        maxQuantityByBaseSku: {},
        maxRingQuantity: 3,
        promotions: [],
        ringCount: 0,
      },
      cartId: null,
      cartIdChangeReason: null,
      resultCategory: '',
    },
  },
};

export const DEFAULT_CONTEXT_VALUES: CartContextValues = {
  handleSetUrlDiscountCode: noop,
  maxAddOnQuantityReachedByBaseSku: {},
  maxChargerQuantityReached: false,
  maxRingQuantityReached: false,
  refreshCart: DEFAULT_REFRESH_CART_VALUES,
  refreshCartError: null,
  refreshCartLoading: true,
  refreshCartSuccess: false,
  sitewideDiscounts: [],
  urlPromotionData: null,
  urlPromotionError: null,
  urlPromotionLoading: true,
  urlPromotionSuccess: false,
  urlDiscountCode: '',
};

export const CartContext = createContext<CartContextValues>(
  DEFAULT_CONTEXT_VALUES,
);

const CartProvider = ({ children }: PropsWithChildren) => {
  const [sitewideDiscounts, setSitewideDiscounts] = useState<
    (SavePromotionResult | null)[]
  >([]);

  const { value: sitewideDiscountConfig } =
    useDynamicConfig('sitewide_discount');
  const sitewideDiscountCodes = (sitewideDiscountConfig['sitewideDiscount'] ??
    '') as string | string[];

  useEffect(() => {
    const applySitewideDiscounts = async () => {
      try {
        const promises = Array.isArray(sitewideDiscountCodes)
          ? sitewideDiscountCodes.map((code) => performSavePromotion(code))
          : [performSavePromotion(sitewideDiscountCodes)];
        const results = await Promise.all(promises);
        setSitewideDiscounts(results);
      } catch (error) {
        logToDatadog({
          error,
          message: 'sitewide discount(s) failed to apply',
          module: 'discount',
        });
      }
    };
    void applySitewideDiscounts();
  }, [sitewideDiscountCodes]);

  const [urlDiscountCode, setUrlDiscountCode] = useState<string>('');
  const urlPromotionData = useSavePromotion(urlDiscountCode);

  // save to localstorage to persist from /discount paths redirect and refresh
  if (
    urlDiscountCode &&
    readFromLocalStorage(PROMOTION_CODE) !== urlDiscountCode
  ) {
    writeToLocalStorage(PROMOTION_CODE, urlDiscountCode);
  }
  const fetchingDiscounts =
    !sitewideDiscounts.length || urlPromotionData.isLoading;

  const refreshCart = useRefreshCart(fetchingDiscounts);
  const cart =
    refreshCart.data?.cart?.refreshCart.cart ??
    DEFAULT_REFRESH_CART_VALUES.cart!.refreshCart.cart!;

  const maxRingQuantityReached = useMemo(() => {
    const maxRingQuantity = cart.maxRingQuantity;
    const ringCount = cart.ringCount;
    if (typeof maxRingQuantity === 'number' && typeof ringCount === 'number') {
      return maxRingQuantity === ringCount;
    } else {
      return false;
    }
  }, [cart]);

  const maxChargerQuantityReached = useMemo(() => {
    const maxChargerQuantity = cart.maxChargerQuantity;
    const chargerCount = cart.chargerCount;
    if (
      typeof maxChargerQuantity === 'number' &&
      typeof chargerCount === 'number'
    ) {
      return maxChargerQuantity === chargerCount;
    } else {
      return false;
    }
  }, [cart]);

  const maxAddOnQuantityReachedByBaseSku = useMemo((): Record<
    string,
    boolean
  > => {
    const maxAddOnQuantityByBaseSku: Record<string, number> =
      (cart.maxAddOnQuantityByBaseSku ?? {}) as Record<string, number>;
    const lineItems =
      cart.lineItems?.filter((lineItem) => lineItem !== null) ?? [];
    return lineItems.reduce((acc, cur) => {
      const skuCode = cur.skuCode;
      if (skuCode) {
        const baseSkus = Object.keys(maxAddOnQuantityByBaseSku);
        const baseSkuForLineItem =
          baseSkus.find((baseSku) => skuCode.startsWith(baseSku)) ?? '';
        const totalQuantityForLineItem = lineItems.reduce(
          (totalQuantity, lineItem) =>
            skuCode === lineItem.skuCode
              ? totalQuantity + (lineItem.quantity ?? 0)
              : totalQuantity,
          0,
        );
        const maxQuantityForLineItem =
          maxAddOnQuantityByBaseSku[baseSkuForLineItem];
        return {
          ...acc,
          [baseSkuForLineItem]:
            maxQuantityForLineItem === totalQuantityForLineItem,
        };
      }
      return acc;
    }, {});
  }, [cart]);

  const onSetUrlDiscountCode = (code: string) => {
    setUrlDiscountCode(code);
  };

  const value: CartContextValues = useMemo(
    () => ({
      handleSetUrlDiscountCode: onSetUrlDiscountCode,
      maxAddOnQuantityReachedByBaseSku,
      maxChargerQuantityReached,
      maxRingQuantityReached,
      refreshCart: refreshCart.data ?? DEFAULT_REFRESH_CART_VALUES,
      refreshCartError: refreshCart.error,
      refreshCartLoading: refreshCart.isLoading,
      refreshCartSuccess: refreshCart.isSuccess,
      sitewideDiscounts,
      urlPromotionData: urlPromotionData.data,
      urlPromotionError: urlPromotionData.error,
      urlPromotionLoading: urlPromotionData.isLoading,
      urlPromotionSuccess: urlPromotionData.isSuccess,
      urlDiscountCode,
    }),
    [
      maxAddOnQuantityReachedByBaseSku,
      maxChargerQuantityReached,
      maxRingQuantityReached,
      refreshCart,
      sitewideDiscounts,
      urlPromotionData,
      urlDiscountCode,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = () => useContext(CartContext);

export default CartProvider;
